import { useState, useMemo, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
// POL-16 fix — this component used to render DialogHeader/DialogTitle
// while EXPECTING a parent <Dialog> that no call site actually provides
// (NewDashboard + Index both mount it bare), which crashed the page
// with "`DialogTitle` must be used within `Dialog`" the moment the
// Add lead button was clicked. It now owns its own Dialog root.
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ServiceType } from '@/types/crm';
import { useCreateInquiry } from '@/services/inquiries';
import { useOrganization } from '@/services/organizations';
import { useInquiryCategories } from '@/services/inquiryCategories';
import { useToast } from '@/hooks/use-toast';
// HOTFIX-4 — "Organization not found" toasts carry a Set up action.
import { ToastAction } from '@/components/ui/toast';
import debug from '@/lib/debug';

interface ManualEntryModalProps {
  onClose: () => void;
  defaultServiceType?: ServiceType;
}

// Fallback for orgs without custom categories
const DEFAULT_SERVICE_OPTIONS: { value: string; label: string }[] = [
  { value: 'general', label: 'General Inquiry' },
];

export const ManualEntryModal = ({ onClose, defaultServiceType }: ManualEntryModalProps) => {
  const { toast } = useToast();
  const createInquiry = useCreateInquiry();
  const { data: organization } = useOrganization();
  const { data: categories = [] } = useInquiryCategories();

  // Build service options from org's inquiry categories.
  //
  // UX-6 — this dropdown reads `inquiry_categories`, the same table
  // ServiceTabs / Inquiries / the public intake form key off, and the
  // same `name → lower_snake` value mapping, so a lead filed here
  // lands under the matching pipeline tab. (The onboarding wizard now
  // actually writes that table — it used to hand its picks to the org
  // hooks, which strip `service_tabs`, which is why this list showed
  // only "General Inquiry" for freshly-onboarded studios.)
  //
  // Fall back to the default AFTER the enabled-filter too: an org
  // whose categories are all disabled used to get an empty dropdown.
  const serviceOptions = useMemo(() => {
    const enabled = categories
      .filter((cat) => cat.enabled)
      .map((cat) => ({
        value: cat.name.trim().toLowerCase().replace(/\s+/g, '_'),
        label: cat.name.trim(),
      }));
    return enabled.length > 0 ? enabled : DEFAULT_SERVICE_OPTIONS;
  }, [categories]);

  const defaultService = defaultServiceType || serviceOptions[0]?.value || 'general';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: defaultService,
    package: '',
    projectDate: '',
    location: '',
    description: '',
    howHeard: '',
  });

  // UX-6 — the categories query can resolve AFTER this modal mounts
  // (cold cache), which used to strand `serviceType` on a value that
  // no longer exists in the options list and render an empty Select.
  // Snap to the first real option when the loaded list doesn't
  // contain the current selection.
  useEffect(() => {
    setFormData((prev) =>
      serviceOptions.some((opt) => opt.value === prev.serviceType)
        ? prev
        : { ...prev, serviceType: serviceOptions[0]?.value || 'general' },
    );
  }, [serviceOptions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      toast({
        title: 'Missing Fields',
        description: 'Name and email are required.',
        variant: 'destructive',
      });
      return;
    }

    if (!organization?.id) {
      toast({
        title: 'Your studio isn’t set up yet',
        description: 'Please complete your studio setup first.',
        variant: 'destructive',
        // HOTFIX-4 — don't dead-end: take them to setup.
        action: (
          <ToastAction
            altText="Complete your studio setup"
            onClick={() => window.location.assign('/onboarding')}
          >
            Set up
          </ToastAction>
        ),
      });
      return;
    }

    try {
      await createInquiry.mutateAsync({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        service_type: formData.serviceType,
        package: formData.package || null,
        project_date: formData.projectDate || null,
        location: formData.location || null,
        description: formData.description || null,
        how_heard: formData.howHeard || null,
        org_id: organization.id,
      });

      toast({
        title: 'Inquiry Created',
        description: 'The inquiry has been added successfully.',
      });
      onClose();
    } catch (error) {
      // UX-6 — say WHY. The bare "Please try again" hid RLS and
      // schema failures behind copy that suggested a network blip.
      console.error('Failed to create inquiry:', error);
      toast({
        title: 'Error',
        description: `Failed to create inquiry — ${debug.parseSupabaseError(error)}`,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add a lead</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-muted-foreground text-sm">Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Who did you meet?"
              className="mt-1"
              required
            />
          </div>
          <div>
            <Label className="text-muted-foreground text-sm">Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
              className="mt-1"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-muted-foreground text-sm">Phone</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+1 (555) 123-4567"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-muted-foreground text-sm">Service</Label>
            <Select
              value={formData.serviceType}
              onValueChange={(v) => setFormData({ ...formData, serviceType: v as ServiceType })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {serviceOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-muted-foreground text-sm">Package/Interest</Label>
            <Input
              value={formData.package}
              onChange={(e) => setFormData({ ...formData, package: e.target.value })}
              placeholder="Premium Package"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-muted-foreground text-sm">Project Date</Label>
            <Input
              type="date"
              value={formData.projectDate}
              onChange={(e) => setFormData({ ...formData, projectDate: e.target.value })}
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label className="text-muted-foreground text-sm">Location</Label>
          <Input
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            placeholder="Los Angeles, CA"
            className="mt-1"
          />
        </div>

        <div>
          <Label className="text-muted-foreground text-sm">How did they hear about us?</Label>
          <Input
            value={formData.howHeard}
            onChange={(e) => setFormData({ ...formData, howHeard: e.target.value })}
            placeholder="Instagram, Referral, Google..."
            className="mt-1"
          />
        </div>

        <div>
          <Label className="text-muted-foreground text-sm">Description</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of the project..."
            className="mt-1 min-h-[80px]"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={createInquiry.isPending}>
            <OrbitIcon name="plus" fallback={Plus} className="w-4 h-4 mr-2" />
            {createInquiry.isPending ? 'Adding...' : 'Add Inquiry'}
          </Button>
        </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
